/**
 * Compact array template (FEAT-2026-0005, Phase 8 form-density polish).
 *
 * Default RJSF / MUI ArrayFieldTemplate stacks each item in its own
 * full-width row with its own label and remove button — for an array
 * like `xTickPositions: [10, 20, 25, 30, 40, 50, 60, 70, 80, 90, 100]`
 * that's 11 rows of ~150 px each, ~1700 px of vertical real estate for
 * what should be one tight line.
 *
 * This template detects "array of primitives" (numbers, strings, integers,
 * booleans) and renders the items as a compact horizontal flex row of
 * ~70-px-wide inputs separated by the move/remove controls. A `+` icon
 * button at the end adds a new item. Result: that same xTickPositions
 * fits on one line.
 *
 * For arrays of OBJECTS (e.g. zoneLabels — which wouldn't actually be
 * editable here since they live in a Record, but defensive), we fall
 * back to a stacked layout that tightens the default RJSF margin without
 * changing the per-item structure.
 */

import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { Add, Close } from '@mui/icons-material';
import type { ArrayFieldTemplateProps } from '@rjsf/utils';
import { PrimitiveArrayItemContext } from './FormSearchContext';

export const ArrayFieldTemplate = (props: ArrayFieldTemplateProps) => {
  const {
    canAdd, items, onAddClick, schema, title, uiSchema, idSchema,
  } = props;

  // Detect "array of primitives" — JSON Schema has THREE shapes for
  // this depending on which JSON-Schema draft Zod targets:
  //
  //   (a) Open-ended array, any draft:
  //         `items: { type: 'number' }`
  //   (b) Tuple, draft-07 (the form Zod v4 uses with target:'draft-7'):
  //         `items: [{type:'integer'}, {type:'integer'}, …]`   ← ARRAY
  //         `minItems: N, maxItems: N`
  //   (c) Tuple, draft-2020-12:
  //         `prefixItems: [{type:'integer'}, …]`
  //         `items: false`
  //
  // The previous fix only handled (a) and (c). We use (b) in production
  // (the backend's getJsonSchema pins target:'draft-7' so RJSF's bundled
  // AJV8 can compile it without the 2020-12 meta-schema), so tuples
  // fell through to the stacked-grey fallback and chartMargin / spacing
  // looked like "4 empty grey lines". Now we handle all three.
  const sch = schema as { items?: unknown; prefixItems?: unknown[]; title?: string; description?: string };
  let itemType: string | undefined;
  // (a) `items` is a single schema object
  if (sch.items && !Array.isArray(sch.items) && typeof sch.items === 'object') {
    itemType = (sch.items as { type?: string }).type;
  }
  // (b) `items` is an array of per-slot schemas (draft-07 tuple)
  if (!itemType && Array.isArray(sch.items) && sch.items.length > 0) {
    const typed = sch.items as Array<{ type?: string }>;
    if (typed.every((s) => s?.type === typed[0]?.type)) {
      itemType = typed[0]?.type;
    }
  }
  // (c) `prefixItems` (draft-2020-12 tuple)
  if (!itemType && Array.isArray(sch.prefixItems) && sch.prefixItems.length > 0) {
    const typed = sch.prefixItems as Array<{ type?: string }>;
    if (typed.every((s) => s?.type === typed[0]?.type)) {
      itemType = typed[0]?.type;
    }
  }
  const effectiveType = itemType;
  const isPrimitive =
    effectiveType === 'number' || effectiveType === 'integer'
    || effectiveType === 'string' || effectiveType === 'boolean';

  // Title / description from schema or uiSchema. Skip the title when the
  // surrounding FieldTemplate already rendered it (RJSF passes a title
  // through both — pick whichever is non-empty).
  const displayTitle = (uiSchema?.['ui:title'] as string | undefined) ?? title ?? sch.title ?? '';
  const description  = (uiSchema?.['ui:description'] as string | undefined) ?? sch.description ?? '';

  if (isPrimitive) {
    return (
      <Box sx={{ mb: 1 }} id={idSchema?.$id}>
        {displayTitle && (
          <Typography
            variant="caption"
            sx={{
              display: 'block', mb: 0.25,
              fontSize: '0.72rem', fontWeight: 500,
              color: 'text.secondary', lineHeight: 1.2,
            }}
          >
            {displayTitle}
          </Typography>
        )}
        {description && (
          <Typography variant="caption" color="text.secondary" sx={{
            display: 'block', mb: 0.5, fontSize: '0.7rem', lineHeight: 1.2,
          }}>
            {description}
          </Typography>
        )}
        <PrimitiveArrayItemContext.Provider value={true}>
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            alignItems: 'center',
            // Tighten EVERY descendant — kills stacked margins inside
            // each item's FieldTemplate / BaseInputTemplate so they
            // sit cleanly inline.
            '& .form-group': { mb: '0 !important' },
            '& .MuiTextField-root': { mb: 0 },
          }}>
            {items.map((item) => (
              <Box
                key={item.key}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.25,
                }}
              >
                <Box sx={{
                  // ~70 px is enough for 3-digit numbers + step buttons.
                  width: effectiveType === 'string' ? 110 : 70,
                  '& input': { fontSize: '0.8rem', py: 0.25 },
                }}>
                  {item.children}
                </Box>
                {item.hasRemove && (
                  <Tooltip title={`Remove item ${item.index + 1}`}>
                    <IconButton
                      size="small"
                      onClick={(e) => item.onDropIndexClick(item.index)(e)}
                      sx={{ p: 0.25 }}
                    >
                      <Close sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            ))}
            {canAdd && (
              <Tooltip title="Add item">
                <IconButton
                  size="small"
                  onClick={onAddClick}
                  sx={{
                    p: 0.5,
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Add fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </PrimitiveArrayItemContext.Provider>
      </Box>
    );
  }

  // ── Fallback for arrays of objects / mixed types ──────────────────────────
  // Same as RJSF's default but with tighter margins.
  return (
    <Box sx={{ mb: 1 }} id={idSchema?.$id}>
      {displayTitle && (
        <Typography
          variant="caption"
          sx={{
            display: 'block', mb: 0.25,
            fontSize: '0.72rem', fontWeight: 500,
            color: 'text.secondary',
          }}
        >
          {displayTitle}
        </Typography>
      )}
      {items.map((item) => (
        <Box
          key={item.key}
          sx={{
            display: 'flex', alignItems: 'flex-start', gap: 0.5,
            mb: 0.5, p: 0.5,
            borderLeft: '2px solid', borderColor: 'divider',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>{item.children}</Box>
          {item.hasRemove && (
            <Tooltip title={`Remove item ${item.index + 1}`}>
              <IconButton
                size="small"
                onClick={(e) => item.onDropIndexClick(item.index)(e)}
                sx={{ p: 0.25 }}
              >
                <Close sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ))}
      {canAdd && (
        <IconButton
          size="small"
          onClick={onAddClick}
          sx={{
            mt: 0.5,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Add fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};
